<template>
  <transition name="fade" mode="out-in">
    <base-modal v-if="visible" :title="modalTitle" height="85vh" @close="$emit('close')">
      <div class="em-cov flex flex-col h-full min-h-0 px-8 pt-4">
        <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
          <p class="text-sm text-gray-500 m-0">
            {{ subtitle }}
          </p>
          <div class="w-full sm:w-72">
            <search ref="searchRef" @on-search="onSearch" class-list="px-4 h-9"></search>
          </div>
        </div>

        <div v-if="loading" class="flex flex-1 min-h-0 items-center justify-center py-16">
          <base-spinner></base-spinner>
        </div>

        <div v-else-if="hasRows" class="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div class="overflow-x-auto overflow-y-auto flex-1 border rounded-lg">
            <table class="min-w-full text-left text-sm">
              <thead class="em-cov-head border-b sticky top-0">
                <tr>
                  <th class="px-4 py-2 text-gray-600">#</th>
                  <th class="px-4 py-2 text-gray-600">Employer</th>
                  <th class="px-4 py-2 text-gray-600">Students</th>
                  <th class="px-4 py-2 text-gray-600">In employer list</th>
                  <th v-if="showAddColumn" class="px-4 py-2 text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, idx) in employers"
                  :key="row.name + '-cov-' + idx"
                  class="border-b border-gray-100"
                >
                  <td class="px-4 py-2 whitespace-nowrap">{{ firstItem + idx }}</td>
                  <td class="px-4 py-2 font-semibold text-gray-900">{{ row.name || "—" }}</td>
                  <td class="px-4 py-2 whitespace-nowrap">{{ formatNumber(row.students_count) }}</td>
                  <td class="px-4 py-2">
                    <span
                      class="em-cov-badge"
                      :class="row.in_employers_catalog ? 'em-cov-badge--yes' : 'em-cov-badge--no'"
                    >
                      {{ row.in_employers_catalog ? "Yes" : "No" }}
                    </span>
                  </td>
                  <td v-if="showAddColumn" class="px-4 py-2">
                    <button
                      v-if="!row.in_employers_catalog"
                      type="button"
                      class="em-cov-add-btn"
                      title="Add to employer list"
                      :disabled="addingName === row.name"
                      @click="addToCatalog(row)"
                    >
                      {{ addingName === row.name ? "Adding…" : "Add" }}
                    </button>
                    <span v-else class="text-gray-400 text-xs">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="mt-3 shrink-0">
            <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
          </div>
        </div>

        <div v-else class="flex flex-1 min-h-0 items-center justify-center py-16 text-gray-500">
          {{ searchText ? "No employers match your search." : "No employers found for this segment." }}
        </div>
      </div>
    </base-modal>
  </transition>
</template>

<script>
import gql from "graphql-tag";
import Pagination from "../../components/shared/pagination.vue";
import Search from "../../components/shared/search.vue";

var LIST_QUERY = gql`
  query ManagedEmployersCoverageDrilldown(
    $first: Int!
    $page: Int!
    $search: String
    $in_employers_catalog: Boolean
  ) {
    managedEmployersList(
      first: $first
      page: $page
      search: $search
      in_employers_catalog: $in_employers_catalog
    ) {
      error
      error_code
      message
      data {
        name
        students_count
        in_employers_catalog
      }
      paginatorInfo {
        currentPage
        lastPage
        perPage
        total
        hasMorePages
        firstItem
        lastItem
      }
    }
  }
`;

var ADD_TO_CATALOG_MUTATION = gql`
  mutation AddManagedEmployerToCatalogFromCoverage($name: String!) {
    addManagedEmployerToCatalog(name: $name) {
      error
      error_code
      message
      data {
        name
        employer_inserted
      }
    }
  }
`;

export default {
  name: "EmployerCoverageDrilldownModal",
  components: {
    Pagination,
    Search,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    inEmployersCatalog: {
      type: Boolean,
      default: false,
    },
    segmentLabel: {
      type: String,
      default: "",
    },
  },
  emits: ["close", "changed"],
  data: function () {
    return {
      loading: false,
      employers: [],
      paginatorInfo: null,
      page: 1,
      perPage: 50,
      searchText: "",
      addingName: "",
    };
  },
  computed: {
    modalTitle: function () {
      if (this.segmentLabel) {
        return "Employer List Coverage — " + this.segmentLabel;
      }
      return this.inEmployersCatalog
        ? "Employer List Coverage — In employer list"
        : "Employer List Coverage — Not in list";
    },
    subtitle: function () {
      if (this.inEmployersCatalog) {
        return "Employer names already saved in the employers table.";
      }
      return "Employer names missing from the employers table. Use Add to save them.";
    },
    showAddColumn: function () {
      return !this.inEmployersCatalog;
    },
    hasRows: function () {
      return this.employers && this.employers.length > 0;
    },
    firstItem: function () {
      if (this.paginatorInfo && this.paginatorInfo.firstItem) {
        return this.paginatorInfo.firstItem;
      }
      return 1;
    },
  },
  watch: {
    visible: function (val) {
      if (val) {
        this.page = 1;
        this.searchText = "";
        this.loadList();
      } else {
        this.employers = [];
        this.paginatorInfo = null;
        this.addingName = "";
      }
    },
    inEmployersCatalog: function () {
      if (this.visible) {
        this.page = 1;
        this.loadList();
      }
    },
    searchText: function () {
      if (!this.visible) {
        return;
      }
      this.page = 1;
      this.loadList();
    },
  },
  methods: {
    formatNumber: function (value) {
      var n = Number(value || 0);
      try {
        return n.toLocaleString();
      } catch (e) {
        return String(n);
      }
    },
    onSearch: function (searchText) {
      this.searchText = searchText;
    },
    setPage: function (p) {
      this.page = p;
      this.loadList();
    },
    toastAlert: function (opts) {
      if (this.$swal) {
        this.$swal.fire(opts);
      }
    },
    async loadList() {
      this.loading = true;
      try {
        var search = (this.searchText || "").trim();
        var result = await this.$apollo.query({
          query: LIST_QUERY,
          variables: {
            first: this.perPage,
            page: this.page,
            search: search ? search : null,
            in_employers_catalog: this.inEmployersCatalog,
          },
          fetchPolicy: "network-only",
        });
        var payload =
          result && result.data && result.data.managedEmployersList
            ? result.data.managedEmployersList
            : null;
        if (!payload) {
          throw new Error("No response");
        }
        if (payload.error) {
          this.toastAlert({
            icon: "error",
            title: "Unable to load",
            text: payload.message,
          });
          this.employers = [];
          this.paginatorInfo = null;
        } else {
          this.employers = payload.data || [];
          this.paginatorInfo = payload.paginatorInfo;
        }
      } catch (e) {
        this.toastAlert({
          icon: "error",
          title: "Failed",
          text: "Could not load employers for this segment.",
        });
        this.employers = [];
        this.paginatorInfo = null;
      } finally {
        this.loading = false;
      }
    },
    async addToCatalog(row) {
      var name = row && row.name ? String(row.name).trim() : "";
      if (!name) {
        return;
      }

      this.addingName = name;
      try {
        var result = await this.$apollo.mutate({
          mutation: ADD_TO_CATALOG_MUTATION,
          variables: { name: name },
        });
        var payload =
          result && result.data && result.data.addManagedEmployerToCatalog
            ? result.data.addManagedEmployerToCatalog
            : null;

        if (!payload) {
          throw new Error("No response");
        }

        if (payload.error) {
          this.toastAlert({
            icon: "error",
            title: "Error",
            text: payload.message,
          });
        } else {
          this.toastAlert({
            icon: "success",
            title: "Added",
            text: payload.message || "Employer added to the list.",
            timer: 2000,
          });
          this.$emit("changed");
          // After add, this row leaves the "Not in list" segment.
          this.loadList();
        }
      } catch (err) {
        this.toastAlert({
          icon: "error",
          title: "Failed",
          text: "Could not add employer to the list.",
        });
      } finally {
        this.addingName = "";
      }
    },
  },
};
</script>

<style scoped>
.em-cov-head {
  background: #f8fafc;
}

.em-cov-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 600;
}
.em-cov-badge--yes {
  background: #d1fae5;
  color: #047857;
}
.em-cov-badge--no {
  background: #f1f5f9;
  color: #64748b;
}

.em-cov-add-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: #1d4ed8;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.15s;
}
.em-cov-add-btn:hover {
  background: #dbeafe;
  color: #1e3a8a;
}
.em-cov-add-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
